import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Logo } from "../components/Logo";
import {
  Q0_FIELD,
  Q0_MICROCOPY,
  SURVEY_INTRO_LINES,
  fieldText,
  profileFromQ0,
  stepsForProfile,
  type ProfileCode,
  type SurveyFieldDef,
  type SurveyStepDef,
} from "../survey/config";
import { evalVisibleIf } from "../survey/flexibleVisibleIf";
import type {
  FlexibleFieldDef,
  FlexibleStepDef,
  FlexibleScreenerDef,
} from "../survey/flexibleSurveyTypes";

/** Pasos estimados de flujo antes de conocer perfil (solo barra intro+q0). */
const EST_FLOW_STEPS = 10;

type Values = Record<string, unknown>;
type CustomQuestion = {
  id: string;
  label: string;
  type: "single" | "multi" | "text_short" | "text_long";
  required?: boolean;
  max_select?: number;
  options?: Array<{ value: string; label: string }>;
};

function getList(v: unknown): string[] {
  return Array.isArray(v) && v.every((x) => typeof x === "string")
    ? (v as string[])
    : [];
}

function toggleList(list: string[], val: string, max?: number): string[] {
  if (list.includes(val)) return list.filter((x) => x !== val);
  if (max !== undefined && list.length >= max) return list;
  return [...list, val];
}

function visibleFields(step: SurveyStepDef, v: Values): SurveyFieldDef[] {
  return step.fields.filter((f) => !f.visibleIf || f.visibleIf(v));
}

function validateField(f: SurveyFieldDef, v: Values): string | null {
  const vis = !f.visibleIf || f.visibleIf(v);
  if (!vis && f.optional) return null;
  if (!vis) return null;

  if (f.type === "single") {
    const s = v[f.id];
    if (f.optional && (s === undefined || s === "")) return null;
    if (!s || typeof s !== "string") return "Elegí una opción.";
    return null;
  }
  if (f.type === "multi") {
    const list = getList(v[f.id]);
    if (list.length < (f.min ?? 1)) return "Marcá al menos una opción.";
    if (f.max !== undefined && list.length > f.max) return `Máximo ${f.max} opciones.`;
    if (f.id === "q3_consequence" && list.includes("nothing") && list.length > 1) {
      return "Si elegís “nada de lo anterior”, desmarcá el resto.";
    }
    return null;
  }
  if (f.type === "text_short" || f.type === "text_long") {
    const s = typeof v[f.id] === "string" ? (v[f.id] as string) : "";
    if (f.optional && !s.trim()) return null;
    if (!f.optional && vis && !s.trim()) return "Completá el texto.";
    if (f.maxLength && s.length > f.maxLength) return `Máximo ${f.maxLength} caracteres.`;
    return null;
  }
  return null;
}

function validateStep(step: SurveyStepDef, v: Values): string | null {
  for (const f of visibleFields(step, v)) {
    const e = validateField(f, v);
    if (e) return e;
  }
  return null;
}

function visibleFlexFields(fields: FlexibleFieldDef[], v: Values): FlexibleFieldDef[] {
  return fields.filter((f) => evalVisibleIf(f.visibleIf, v));
}

function validateFlexField(f: FlexibleFieldDef, v: Values): string | null {
  const vis = !f.visibleIf || evalVisibleIf(f.visibleIf, v);
  if (!vis && f.optional) return null;
  if (!vis) return null;

  if (f.type === "single") {
    const s = v[f.id];
    if (f.optional && (s === undefined || s === "")) return null;
    if (!s || typeof s !== "string") return "Elegí una opción.";
    return null;
  }
  if (f.type === "multi") {
    const list = getList(v[f.id]);
    if (f.optional && list.length === 0) return null;
    if (list.length < (f.min ?? 1)) return "Marcá al menos una opción.";
    if (f.max !== undefined && list.length > f.max) return `Máximo ${f.max} opciones.`;
    return null;
  }
  if (f.type === "scale") {
    const s = v[f.id];
    if (f.optional && (s === undefined || s === "")) return null;
    if (typeof s !== "string" || !s.trim()) return "Elegí un valor.";
    const n = parseInt(s, 10);
    const lo = f.scaleMin ?? 1;
    const hi = f.scaleMax ?? 10;
    if (!Number.isFinite(n) || n < lo || n > hi) return "Valor fuera de rango.";
    return null;
  }
  if (f.type === "text_short" || f.type === "text_long") {
    const s = typeof v[f.id] === "string" ? (v[f.id] as string) : "";
    if (f.optional && !s.trim()) return null;
    if (!f.optional && vis && !s.trim()) return "Completá el texto.";
    if (f.maxLength && s.length > f.maxLength) return `Máximo ${f.maxLength} caracteres.`;
    return null;
  }
  return null;
}

function validateFlexStep(step: FlexibleStepDef, v: Values): string | null {
  for (const f of visibleFlexFields(step.fields, v)) {
    const e = validateFlexField(f, v);
    if (e) return e;
  }
  return null;
}

const DEFAULT_TAB_TITLE = "Encuesta";

export default function SurveyPage() {
  const { slug } = useParams<{ slug?: string }>();
  const tabTitleBaseline = useRef<string | null>(null);
  const [phase, setPhase] = useState<"intro" | "q0" | "flow" | "final">(
    "intro",
  );
  const [profile, setProfile] = useState<ProfileCode | null>(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [values, setValues] = useState<Values>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [questionLabels, setQuestionLabels] = useState<Record<string, string>>({});
  const [customQuestions, setCustomQuestions] = useState<CustomQuestion[]>([]);
  const [surveyTemplate, setSurveyTemplate] = useState<"vinculos" | "flexible_v1">(
    "vinculos",
  );
  const [introLines, setIntroLines] = useState<string[]>(() => [...SURVEY_INTRO_LINES]);
  const [flexScreener, setFlexScreener] = useState<FlexibleScreenerDef | null>(null);
  const [flexFlow, setFlexFlow] = useState<FlexibleStepDef[]>([]);

  const isFlex = surveyTemplate === "flexible_v1" && flexScreener && flexFlow.length > 0;

  const neutral = profile === "p2n";
  const steps = useMemo(
    () => (profile ? stepsForProfile(profile) : []),
    [profile],
  );

  const totalWithFlow = 2 + steps.length + 1;
  const totalPreProfile = 2 + EST_FLOW_STEPS + 1;
  const flexTotalUnits = 2 + flexFlow.length + 1;
  const progress = useMemo(() => {
    if (isFlex) {
      if (phase === "intro") return Math.round((1 / flexTotalUnits) * 100);
      if (phase === "q0") return Math.round((2 / flexTotalUnits) * 100);
      if (phase === "flow") {
        return Math.round(((2 + stepIdx + 1) / flexTotalUnits) * 100);
      }
      return 100;
    }
    if (phase === "intro") {
      return Math.round((1 / totalPreProfile) * 100);
    }
    if (phase === "q0") {
      return Math.round((2 / totalPreProfile) * 100);
    }
    if (phase === "flow") {
      return Math.round(((2 + stepIdx + 1) / totalWithFlow) * 100);
    }
    return 100;
  }, [
    isFlex,
    phase,
    stepIdx,
    totalWithFlow,
    totalPreProfile,
    flexTotalUnits,
  ]);

  useEffect(() => {
    if (tabTitleBaseline.current === null) {
      tabTitleBaseline.current = document.title || DEFAULT_TAB_TITLE;
    }
    document.title = tabTitleBaseline.current ?? DEFAULT_TAB_TITLE;
    const key = (slug || "vinculos").trim();
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/survey/${encodeURIComponent(key)}`);
        if (!res.ok) return;
        const data = (await res.json()) as {
          template?: string;
          intro_lines?: string[];
          screener?: FlexibleScreenerDef;
          flow?: FlexibleStepDef[];
          question_labels?: Record<string, string>;
          custom_questions?: CustomQuestion[];
          share?: { title: string; description: string };
        };
        if (!cancelled && data.share?.title) {
          document.title = data.share.title;
        }
        if (!cancelled) {
          if (data.template === "flexible_v1") {
            setSurveyTemplate("flexible_v1");
          } else {
            setSurveyTemplate("vinculos");
          }
          if (Array.isArray(data.intro_lines) && data.intro_lines.length > 0) {
            setIntroLines(data.intro_lines.filter((s) => typeof s === "string" && s.trim()));
          } else if (data.template !== "flexible_v1") {
            setIntroLines([...SURVEY_INTRO_LINES]);
          }
          if (
            data.template === "flexible_v1" &&
            data.screener &&
            data.flow &&
            Array.isArray(data.flow)
          ) {
            setFlexScreener(data.screener);
            setFlexFlow(data.flow);
            if (data.flow.length === 0) {
              setErr("Esta encuesta flexible no tiene pasos configurados.");
            }
          } else {
            setFlexScreener(null);
            setFlexFlow([]);
            if (data.template === "flexible_v1") {
              setErr("Esta encuesta no está disponible (definición incompleta).");
            }
          }
        }
        if (!cancelled && data.question_labels && typeof data.question_labels === "object") {
          setQuestionLabels(data.question_labels);
        }
        if (!cancelled && Array.isArray(data.custom_questions)) {
          setCustomQuestions(
            data.custom_questions
              .filter((q) => /^cq_[a-z0-9_]+$/.test(q.id))
              .slice(0, 20),
          );
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
      document.title = tabTitleBaseline.current ?? DEFAULT_TAB_TITLE;
    };
  }, [slug]);

  function labelFor(id: string, fallback: string): string {
    const v = questionLabels[id];
    return typeof v === "string" && v.trim() ? v : fallback;
  }

  function setField(id: string, val: unknown) {
    setValues((prev) => ({ ...prev, [id]: val }));
  }

  function onMulti(f: SurveyFieldDef, val: string) {
    setValues((prev) => {
      const cur = getList(prev[f.id]);
      let next = toggleList(cur, val, f.max);
      if (f.id === "q3_consequence") {
        if (val === "nothing") next = ["nothing"];
        else next = next.filter((x) => x !== "nothing");
      }
      const out: Values = { ...prev, [f.id]: next };
      if (f.id === "q4_situation" && !next.includes("other")) {
        out.q4b_other_text = "";
      }
      if (f.id === "q6_what_breaks" && !next.includes("other")) {
        out.q6b_break_other = "";
      }
      return out;
    });
  }

  function flexOnMulti(f: FlexibleFieldDef, val: string) {
    setValues((prev) => {
      const cur = getList(prev[f.id]);
      const next = toggleList(cur, val, f.max);
      return { ...prev, [f.id]: next };
    });
  }

  function startFlow() {
    const q0 = values.q0_profile;
    if (!q0 || typeof q0 !== "string") {
      setErr("Elegí una opción.");
      return;
    }
    setErr(null);
    setProfile(profileFromQ0(q0));
    setPhase("flow");
    setStepIdx(0);
  }

  function startFlexFlow() {
    if (!flexScreener) return;
    const q0 = values[flexScreener.id];
    if (!q0 || typeof q0 !== "string") {
      setErr("Elegí una opción.");
      return;
    }
    setErr(null);
    setValues((prev) => ({ ...prev, q0_profile: q0 }));
    setPhase("flow");
    setStepIdx(0);
  }

  function nextFlow() {
    const step = steps[stepIdx];
    if (!step) return;
    const e = validateStep(step, values);
    if (e) {
      setErr(e);
      return;
    }
    setErr(null);
    if (stepIdx < steps.length - 1) setStepIdx(stepIdx + 1);
    else setPhase("final");
  }

  function nextFlexFlow() {
    const step = flexFlow[stepIdx];
    if (!step) return;
    const e = validateFlexStep(step, values);
    if (e) {
      setErr(e);
      return;
    }
    setErr(null);
    if (stepIdx < flexFlow.length - 1) setStepIdx(stepIdx + 1);
    else setPhase("final");
  }

  function back() {
    setErr(null);
    if (isFlex) {
      if (phase === "final") {
        setPhase("flow");
        setStepIdx(flexFlow.length - 1);
        return;
      }
      if (phase === "flow") {
        if (stepIdx > 0) setStepIdx(stepIdx - 1);
        else setPhase("q0");
        return;
      }
      if (phase === "q0") {
        setPhase("intro");
      }
      return;
    }
    if (phase === "final") {
      setPhase("flow");
      setStepIdx(steps.length - 1);
      return;
    }
    if (phase === "flow") {
      if (stepIdx > 0) setStepIdx(stepIdx - 1);
      else {
        setPhase("q0");
        setProfile(null);
      }
      return;
    }
    if (phase === "q0") {
      setPhase("intro");
    }
  }

  async function submitFlex() {
    if (!values.consent) {
      setErr("Se requiere consentimiento.");
      return;
    }
    setErr(null);
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        profile: "fx",
        survey_slug: slug || "vinculos",
        consent: true,
        website: values.website ?? "",
        ...values,
      };
      if (typeof values.optin_email === "string" && values.optin_email.trim()) {
        body.contact_email = values.optin_email.trim();
      }
      const res = await fetch("/api/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const raw = await res.text();
      let data: { error?: string } = {};
      try {
        data = raw ? (JSON.parse(raw) as { error?: string }) : {};
      } catch {
        setErr(res.ok ? "Respuesta inválida del servidor." : `No se pudo enviar (${res.status}).`);
        return;
      }
      if (!res.ok) {
        setErr(data.error || "No se pudo enviar.");
        return;
      }
      setDone(true);
    } catch {
      setErr("Error de red.");
    } finally {
      setSubmitting(false);
    }
  }

  async function submit() {
    if (!profile) return;
    for (const q of customQuestions) {
      if (!q.required) continue;
      const val = values[q.id];
      if (q.type === "multi") {
        const list = getList(val);
        if (list.length < 1) {
          setErr(`Completá: ${q.label}`);
          return;
        }
      } else {
        if (typeof val !== "string" || !val.trim()) {
          setErr(`Completá: ${q.label}`);
          return;
        }
      }
    }
    if (!values.consent) {
      setErr("Se requiere consentimiento.");
      return;
    }
    setErr(null);
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        profile,
        survey_slug: slug || "vinculos",
        q0_profile: values.q0_profile,
        consent: true,
        website: values.website ?? "",
        ...values,
      };
      if (typeof values.optin_email === "string" && values.optin_email.trim()) {
        body.contact_email = values.optin_email.trim();
      }
      const res = await fetch("/api/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const raw = await res.text();
      let data: { error?: string } = {};
      try {
        data = raw ? (JSON.parse(raw) as { error?: string }) : {};
      } catch {
        setErr(res.ok ? "Respuesta inválida del servidor." : `No se pudo enviar (${res.status}).`);
        return;
      }
      if (!res.ok) {
        setErr(data.error || "No se pudo enviar.");
        return;
      }
      setDone(true);
    } catch {
      setErr("Error de red.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <main className="pp-shell flex flex-col items-center justify-center gap-8 p-6">
        <Logo variant="icon" iconSize={56} aria-label="Encuesta" />
        <p className="text-center text-pp-main max-w-md text-lg font-medium">
          Gracias. Tu respuesta fue registrada.
        </p>
      </main>
    );
  }

  return (
    <main className="pp-shell p-4 pb-12 max-w-lg mx-auto">
      <header className="mb-8 flex flex-wrap items-center gap-3 border-b border-pp-border pb-6">
        <Logo variant="icon" iconSize={40} className="shrink-0" aria-label="Encuesta" />
        <span className="pp-badge-soft text-[10px]">Encuesta</span>
      </header>
      <div
        className="mb-6"
        role="progressbar"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Avance"
      >
        <div className="pp-progress-track">
          <div className="pp-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <p className="mt-2 text-sm text-pp-muted text-right">{progress}%</p>
      </div>

      {err && (
        <p className="mb-4 text-sm text-rose-600" role="alert">
          {err}
        </p>
      )}

      {phase === "intro" && (
        <div className="space-y-4 text-sm text-pp-main leading-relaxed">
          {introLines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      )}

      {phase === "q0" && isFlex && flexScreener && (
        <fieldset>
          <legend className="text-sm font-medium text-pp-main mb-3 block">
            {flexScreener.label}
          </legend>
          <div className="space-y-2">
            {flexScreener.options.map((o) => (
              <label key={o.value} className="flex gap-2 items-start cursor-pointer">
                <input
                  type="radio"
                  name={flexScreener.id}
                  checked={values[flexScreener.id] === o.value}
                  onChange={() => setField(flexScreener.id, o.value)}
                  className="mt-1"
                />
                <span className="text-sm">{o.label}</span>
              </label>
            ))}
          </div>
          {flexScreener.microcopy ? (
            <p className="mt-3 text-xs text-pp-quiet">{flexScreener.microcopy}</p>
          ) : null}
        </fieldset>
      )}

      {phase === "q0" && !isFlex && (
        <fieldset>
          <legend className="text-sm font-medium text-pp-main mb-3 block">
            {labelFor(Q0_FIELD.id, Q0_FIELD.label)}
          </legend>
          <div className="space-y-2">
            {Q0_FIELD.options?.map((o) => (
              <label key={o.value} className="flex gap-2 items-start cursor-pointer">
                <input
                  type="radio"
                  name="q0_profile"
                  checked={values.q0_profile === o.value}
                  onChange={() => setField("q0_profile", o.value)}
                  className="mt-1"
                />
                <span className="text-sm">{o.label}</span>
              </label>
            ))}
          </div>
          <p className="mt-3 text-xs text-pp-quiet">{Q0_MICROCOPY}</p>
        </fieldset>
      )}

      {phase === "flow" && isFlex && flexFlow[stepIdx] && (
        <section className="space-y-8">
          {visibleFlexFields(flexFlow[stepIdx].fields, values).map((f) => (
            <fieldset key={f.id}>
              <legend className="text-sm font-medium text-pp-main mb-2 block">{f.label}</legend>
              {f.type === "single" && f.options && (
                <div className="space-y-2">
                  {f.options.map((o) => (
                    <label key={o.value} className="flex gap-2 items-start cursor-pointer">
                      <input
                        type="radio"
                        name={f.id}
                        checked={values[f.id] === o.value}
                        onChange={() => setField(f.id, o.value)}
                        className="mt-1"
                      />
                      <span className="text-sm">{o.label}</span>
                    </label>
                  ))}
                </div>
              )}
              {f.type === "multi" && f.options && (
                <div className="space-y-2">
                  {f.options.map((o) => {
                    const list = getList(values[f.id]);
                    return (
                      <label key={o.value} className="flex gap-2 items-start cursor-pointer">
                        <input
                          type="checkbox"
                          checked={list.includes(o.value)}
                          onChange={() => flexOnMulti(f, o.value)}
                          className="mt-1"
                        />
                        <span className="text-sm">{o.label}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {f.type === "scale" && (
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    const lo = f.scaleMin ?? 1;
                    const hi = f.scaleMax ?? 10;
                    const st = f.scaleStep ?? 1;
                    const opts: number[] = [];
                    for (let i = lo; i <= hi; i += st) opts.push(i);
                    return opts.map((n) => (
                      <label
                        key={n}
                        className="inline-flex items-center gap-1 border rounded px-2 py-1 cursor-pointer text-sm"
                      >
                        <input
                          type="radio"
                          name={f.id}
                          checked={values[f.id] === String(n)}
                          onChange={() => setField(f.id, String(n))}
                        />
                        {n}
                      </label>
                    ));
                  })()}
                </div>
              )}
              {(f.type === "text_short" || f.type === "text_long") &&
                (f.type === "text_long" ? (
                  <textarea
                    rows={4}
                    maxLength={f.maxLength ?? 500}
                    value={
                      typeof values[f.id] === "string" ? (values[f.id] as string) : ""
                    }
                    onChange={(e) => setField(f.id, e.target.value)}
                    className="pp-input-field"
                  />
                ) : (
                  <input
                    type="text"
                    maxLength={f.maxLength ?? 120}
                    value={
                      typeof values[f.id] === "string" ? (values[f.id] as string) : ""
                    }
                    onChange={(e) => setField(f.id, e.target.value)}
                    className="pp-input-field"
                  />
                ))}
            </fieldset>
          ))}
        </section>
      )}

      {phase === "flow" && !isFlex && steps[stepIdx] && (
        <section className="space-y-8">
          {visibleFields(steps[stepIdx], values).map((f) => (
            <fieldset key={f.id}>
              <legend className="text-sm font-medium text-pp-main mb-2 block">
                {labelFor(f.id, fieldText(f, neutral))}
              </legend>
              {f.type === "single" && f.options && (
                <div className="space-y-2">
                  {f.options.map((o) => (
                    <label
                      key={o.value}
                      className="flex gap-2 items-start cursor-pointer"
                    >
                      <input
                        type="radio"
                        name={f.id}
                        checked={values[f.id] === o.value}
                        onChange={() => setField(f.id, o.value)}
                        className="mt-1"
                      />
                      <span className="text-sm">{o.label}</span>
                    </label>
                  ))}
                </div>
              )}
              {f.type === "multi" && f.options && (
                <div className="space-y-2">
                  {f.options.map((o) => {
                    const list = getList(values[f.id]);
                    return (
                      <label
                        key={o.value}
                        className="flex gap-2 items-start cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={list.includes(o.value)}
                          onChange={() => onMulti(f, o.value)}
                          className="mt-1"
                        />
                        <span className="text-sm">{o.label}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {(f.type === "text_short" || f.type === "text_long") && (
                f.type === "text_long" ? (
                  <textarea
                    rows={4}
                    maxLength={f.maxLength ?? 500}
                    value={
                      typeof values[f.id] === "string"
                        ? (values[f.id] as string)
                        : ""
                    }
                    onChange={(e) => setField(f.id, e.target.value)}
                    className="pp-input-field"
                  />
                ) : (
                  <input
                    type="text"
                    maxLength={f.maxLength ?? 120}
                    value={
                      typeof values[f.id] === "string"
                        ? (values[f.id] as string)
                        : ""
                    }
                    onChange={(e) => setField(f.id, e.target.value)}
                    className="pp-input-field"
                  />
                )
              )}
            </fieldset>
          ))}
        </section>
      )}

      {phase === "final" && isFlex && (
        <section className="space-y-6 relative">
          <div className="absolute -left-[9999px] w-px h-px overflow-hidden" aria-hidden>
            <input
              tabIndex={-1}
              autoComplete="off"
              value={typeof values.website === "string" ? values.website : ""}
              onChange={(e) => setField("website", e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="optin_email" className="text-sm font-medium block mb-2">
              {labelFor(
                "optin_email",
                "Email de contacto (opcional).",
              )}
            </label>
            <input
              id="optin_email"
              type="email"
              autoComplete="email"
              value={typeof values.optin_email === "string" ? values.optin_email : ""}
              onChange={(e) => setField("optin_email", e.target.value)}
              className="pp-input-field"
            />
          </div>
          <label className="flex gap-2 items-start cursor-pointer">
            <input
              type="checkbox"
              checked={values.consent === true}
              onChange={(e) => setField("consent", e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm text-pp-main">
              {labelFor(
                "consent",
                "Acepto participar según los términos indicados en la intro.",
              )}
            </span>
          </label>
        </section>
      )}

      {phase === "final" && !isFlex && (
        <section className="space-y-6 relative">
          {customQuestions.length > 0 && (
            <div className="space-y-4">
              <p className="text-sm font-medium text-pp-main">
                Preguntas específicas de tu rubro
              </p>
              {customQuestions.map((q) => (
                <fieldset key={q.id} className="space-y-2">
                  <legend className="text-sm font-medium text-pp-main mb-1 block">
                    {q.label}
                    {q.required ? "" : " (opcional)"}
                  </legend>
                  {q.type === "single" && q.options && (
                    <div className="space-y-2">
                      {q.options.map((o) => (
                        <label key={o.value} className="flex gap-2 items-start cursor-pointer">
                          <input
                            type="radio"
                            name={q.id}
                            checked={values[q.id] === o.value}
                            onChange={() => setField(q.id, o.value)}
                            className="mt-1"
                          />
                          <span className="text-sm">{o.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {q.type === "multi" && q.options && (
                    <div className="space-y-2">
                      {q.options.map((o) => {
                        const list = getList(values[q.id]);
                        return (
                          <label key={o.value} className="flex gap-2 items-start cursor-pointer">
                            <input
                              type="checkbox"
                              checked={list.includes(o.value)}
                              onChange={() => {
                                const max = q.max_select;
                                const next = toggleList(list, o.value, max);
                                setField(q.id, next);
                              }}
                              className="mt-1"
                            />
                            <span className="text-sm">{o.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {(q.type === "text_short" || q.type === "text_long") &&
                    (q.type === "text_long" ? (
                      <textarea
                        rows={4}
                        maxLength={500}
                        value={typeof values[q.id] === "string" ? (values[q.id] as string) : ""}
                        onChange={(e) => setField(q.id, e.target.value)}
                        className="pp-input-field"
                      />
                    ) : (
                      <input
                        type="text"
                        maxLength={220}
                        value={typeof values[q.id] === "string" ? (values[q.id] as string) : ""}
                        onChange={(e) => setField(q.id, e.target.value)}
                        className="pp-input-field"
                      />
                    ))}
                </fieldset>
              ))}
            </div>
          )}
          {profile !== "p1" && (
            <div>
              <label htmlFor="q12_open" className="text-sm font-medium block mb-2">
                {labelFor(
                  "q12_open",
                  "Si pudieras hacer que una sola cosa sea más fácil en la coordinación, ¿cuál sería? (opcional)",
                )}
              </label>
              <textarea
                id="q12_open"
                rows={3}
                maxLength={240}
                value={typeof values.q12_open === "string" ? values.q12_open : ""}
                onChange={(e) => setField("q12_open", e.target.value)}
                className="pp-input-field"
              />
            </div>
          )}
          {profile === "p1" && (
            <div>
              <label htmlFor="p1q3_open" className="text-sm font-medium block mb-2">
                {labelFor("p1q3_open", "¿Qué les gustaría que fuera más fácil? (opcional)")}
              </label>
              <textarea
                id="p1q3_open"
                rows={3}
                maxLength={240}
                value={typeof values.p1q3_open === "string" ? values.p1q3_open : ""}
                onChange={(e) => setField("p1q3_open", e.target.value)}
                className="pp-input-field"
              />
            </div>
          )}
          <div className="absolute -left-[9999px] w-px h-px overflow-hidden" aria-hidden>
            <input
              tabIndex={-1}
              autoComplete="off"
              value={typeof values.website === "string" ? values.website : ""}
              onChange={(e) => setField("website", e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="optin_email" className="text-sm font-medium block mb-2">
              {labelFor(
                "optin_email",
                "Si querés recibir un resumen de resultados o participar de una prueba guiada, dejá un email (opcional).",
              )}
            </label>
            <input
              id="optin_email"
              type="email"
              autoComplete="email"
              value={typeof values.optin_email === "string" ? values.optin_email : ""}
              onChange={(e) => setField("optin_email", e.target.value)}
              className="pp-input-field"
            />
          </div>
          <label className="flex gap-2 items-start cursor-pointer">
            <input
              type="checkbox"
              checked={values.consent === true}
              onChange={(e) => setField("consent", e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm text-pp-main">
              {labelFor(
                "consent",
                "Entiendo que los datos son anónimos y no incluyen información sensible de menores; acepto participar.",
              )}
            </span>
          </label>
        </section>
      )}

      <div className="mt-8 flex gap-3 justify-between">
        <button
          type="button"
          onClick={back}
          disabled={submitting || phase === "intro"}
          className="pp-btn-secondary disabled:opacity-40"
        >
          Atrás
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={() => {
            if (phase === "intro") {
              setErr(null);
              setPhase("q0");
            } else if (phase === "q0") {
              if (isFlex) startFlexFlow();
              else startFlow();
            } else if (phase === "flow") {
              if (isFlex) nextFlexFlow();
              else nextFlow();
            } else if (isFlex) {
              void submitFlex();
            } else {
              void submit();
            }
          }}
          className="pp-btn-primary disabled:opacity-50"
        >
          {phase === "final"
            ? submitting
              ? "Enviando…"
              : "Enviar"
            : "Siguiente"}
        </button>
      </div>
    </main>
  );
}
